import { Component, ViewEncapsulation, ViewChild, OnInit, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { take } from 'rxjs/operators';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlattener, MatTreeFlatDataSource } from '@angular/material/tree';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { ItemNode, FlatNode, ChipFilter } from 'src/models/schedule.model';
import { DateTime as LuxonDateTime } from 'luxon';
import { MatTableDataSource } from '@angular/material/table';

import { Client } from 'src/providers/client/client';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { CurrentAddress } from 'src/providers/addresses/current-address';
import { Invoices } from 'src/providers/invoices/invoices';
import { Pros } from 'src/providers/pros/pros';
import { WindowService } from 'src/shared/providers/window.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { Util } from 'src/shared/util/util';

import { Loading } from 'src/shared/components/loading/loading';

import { InvoiceFilterParams } from 'src/models/invoice.model';

import { TidyCurrencyPipe } from 'src/shared/pipes/tidy-currency.pipe';
import { TidyAbbreviateLastName } from 'src/shared/pipes/tidy-abbr-lastname.pipe';
import { getSellerDisplay } from 'src/shared/util/get-seller-display';

import { BillSettingsPage } from 'src/pages/bills/bills-settings/bills-settings';
import { PaidInvoicePage } from 'src/pages/invoices/paid-invoice/paid-invoice';
import { PendingInvoicePage } from 'src/pages/invoices/pending-invoice/pending-invoice';
import { CreateInvoicePage } from 'src/pages/invoices/create-invoice/create-invoice';
import { MyProPage } from 'src/pages/my-pros/my-pro/my-pro';
import { AboutThisPageService } from 'src/providers/about-this-page/about-this-page.service';
import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';
import { OnboardingDummyDataService } from 'src/shared/services/onboarding-dummy-data.service';
import { InfiniteScrollCustomEvent } from '@ionic/core';

import { ParseDateTimePipe } from 'src/shared/pipes/parse-date.pipe';
import { AgentsProvider } from 'src/providers/agents/agents';
import { Events } from 'src/providers/events/events';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';

@Component({
    templateUrl: 'bills.html',
    styleUrls: ['bills.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class BillsPage implements OnInit {
  private destroyRef = inject(DestroyRef);

  addressResponse: any;
  addressFilter: any;
  bills: any = [];
  errorMessage: string;
  isGettingFilteredBills = false;
  private lastBillsFetchTime = 0;
  private lastBillsFetchKey = '';
  private readonly BILLS_CACHE_DURATION_MS = 2000;
  issues: any;
  loggedIssuesResponse: any;
  showLoadingSpinner: boolean;
  statusItems = [
    {
      viewValue: new TranslationPipe().transform('Pending'),
      value: 'pending'
    },
    {
      viewValue: new TranslationPipe().transform('Paid'),
      value: 'paid'
    }
  ];
  headers: any = ['Property', 'Pro', 'Status', 'Job', 'Job Date', 'Total Amount', 'last-column'];
  rows: any;
  currentRows: any;
  form: UntypedFormGroup;
  dateRangeForm: UntypedFormGroup;
  loaded: boolean;
  pageSize: any;
  pageIndex: any;
  proList: any[] = [];
  routeReused: boolean;
  selectedAddresses: number[];
  selectedPros: number[];
  selectOptions: any;
  totalRows: any;
  filters: Partial<InvoiceFilterParams> = {};
  isDataLoaded = false;
  @ViewChild('tidyTable') tidyTable: any;
  backPage = 'more';
  didCheckOnboarding: boolean;

  private _transformer = (node: ItemNode, level: number) => ({
      expandable: !!node.children && node.children.length > 0,
      name: node.name,
      level,
      data: node,
    });

  treeControl = new FlatTreeControl<FlatNode>(
    (node) => node.level,
    (node) => node.expandable
  );

  treeFlattener = new MatTreeFlattener(
    this._transformer,
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.children
  );

  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  chips: ChipFilter[] = [{
    displayParentName: 'Status',
    parentName: 'status',
    name: 'Pending',
    value: 'pending',
    icon: 'flag',
  }];
  selectedFilters: {
    status: string[];
    pro: number[];
    propertyName: number[];
  } = {
    status: ['pending'],
    pro: [],
    propertyName: []
  };
  filterNamesReferences = {
    'Status': 'status',
    'Pro': 'pro',
    'Property': 'propertyName'
  };
  showAboutThisPage: boolean;

  constructor(
    private client: Client,
    private currentAddress: CurrentAddress,
    private invoices: Invoices,
    private navCtrl: CustomNavController,
    private pros: Pros,
    private rightSidePanelService: RightSidePanelService,
    private storage: TidyStorage,
    public windowService: WindowService,
    private fb: FormBuilder,
    private util: Util,
    private onboardingDummyDataService: OnboardingDummyDataService,
    private aboutThisPageService: AboutThisPageService,
    private onboardingProvider: OnboardingProvider,
    private agentsProvider: AgentsProvider,
    private events: Events,
  ) {
    this.form = this.fb.group({
      selectedPro: [''],
      selectedAddress: [''],
      selectedStatus: ['pending'],
    });
    this.dateRangeForm = fb.group({
      start: '',
      end: '',
      customStringSelect: ''
    });
    this.selectOptions = [
      { viewValue: new TranslationPipe().transform('Custom'), value: 'custom' },
      { viewValue: new TranslationPipe().transform('Today'), value: 'today' },
      { viewValue: new TranslationPipe().transform('Last Week'), value: 'last_week' },
      { viewValue: new TranslationPipe().transform('Last Month'), value: 'last_month' },
      { viewValue: new TranslationPipe().transform('All Time'), value: 'all_time' }
    ];
    this.showAboutThisPage = this.aboutThisPageService.checkIfShouldShowAboutThisPage('bills');
  }

  async ngOnInit() {
    const cameFrom = localStorage.getItem('cameFromPage');
    if (cameFrom === 'properties' || cameFrom === 'more') {
      this.backPage = cameFrom;
    }
    try {
      await this.loadPage();
      this.openBillPage();
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  async loadPage(showLoadingSpinner = false, setStoredRoute = true) {
    if (showLoadingSpinner) {
      this.showLoadingSpinner = true;
    }
    this.showAboutThisPage = this.aboutThisPageService.checkIfShouldShowAboutThisPage('bills');
    this.bills = [];
    await this.getPageDataNoLoadingIndicator();
    this.showLoadingSpinner = false;
    if (this.windowService.isDesktopRes && setStoredRoute) {
      this.storage.setStoredRoute('/bills').then(async () => {
        this.bills = [];
        this.routeReused = true;
        await this.loadPage(true);
      });
    }
  }

  dismissAboutThisPage() {
    this.showAboutThisPage = false;
    this.bills = [];
    this.currentRows = [];
    this.rows = [];
    this.lastBillsFetchKey = '';
    this.lastBillsFetchTime = 0;
    this.loadPage(true, false);
  }

  async openBillPage() {
    if (this.showAboutThisPage) {
      return;
    }
    const shouldOpenBillPageID = await this.storage.retrieve('shouldOpenBillPageID');
    if (shouldOpenBillPageID) {
      let bill = this.bills.find((item) => item.id === shouldOpenBillPageID);
      if (!bill) {
        bill = await this.invoices.getInvoiceById(shouldOpenBillPageID);
      }
      this.goToInvoicePage(bill);
      await this.storage.delete('shouldOpenBillPageID')
    }
  }

  async getPageDataNoLoadingIndicator() {
    if (!this.routeReused) {
      this.isDataLoaded = false;
    }
    this.chips = [{
      displayParentName: 'Status',
      parentName: 'status',
      name: 'Pending',
      value: 'pending',
      icon: 'flag',
    }];
    this.selectedFilters = {
      status: ['pending'],
      pro: [],
      propertyName: []
    };
    this.filters = {
      status: ['pending'],
      team_ids: null,
      jobs_since_date: null,
      jobs_until_date: null,
      address_ids: null
    };
    const savedAddressFilter = localStorage.getItem('billsAddressFilter');
    if (savedAddressFilter) {
      const addressIds = JSON.parse(savedAddressFilter).map((id: any) => Number(id));
      this.filters.address_ids = addressIds;
      this.selectedFilters.propertyName = addressIds;
      localStorage.removeItem('billsAddressFilter');
      localStorage.setItem('isAllAddressesSelected', 'false');
    } else {
      localStorage.setItem('isAllAddressesSelected', 'true');
    }
    this.pageSize = 25;
    this.pageIndex = 1;
    this.form.reset();
    this.buildHeaders();
    await this.getInitialData();
    setTimeout(() => {
      if (this.tidyTable?.paginator) {
        this.tidyTable.paginator.length = this.totalRows;
      }
    }, 1000);
    this.loaded = true;
  }

  @Loading('', true)
  async getPageData() {
    this.chips = [{
      displayParentName: 'Status',
      parentName: 'status',
      name: 'Pending',
      value: 'pending',
      icon: 'flag',
    }];
    this.selectedFilters = {
      status: ['pending'],
      pro: [],
      propertyName: []
    };
    this.filters = {
      status: ['pending'],
      team_ids: null,
      jobs_since_date: null,
      jobs_until_date: null,
      address_ids: null
    };
    const savedAddressFilter = localStorage.getItem('billsAddressFilter');
    if (savedAddressFilter) {
      const addressIds = JSON.parse(savedAddressFilter).map((id: any) => Number(id));
      this.filters.address_ids = addressIds;
      this.selectedFilters.propertyName = addressIds;
      localStorage.removeItem('billsAddressFilter');
      localStorage.setItem('isAllAddressesSelected', 'false');
    } else {
      localStorage.setItem('isAllAddressesSelected', 'true');
    }
    this.pageSize = 25;
    this.pageIndex = 1;
    this.form.reset();
    this.buildHeaders();
    await this.getInitialData();
    setTimeout(() => {
      if (this.tidyTable?.paginator) {
        this.tidyTable.paginator.length = this.totalRows;
      }
    }, 500);
    this.loaded = true;
  }

  async getInitialData(): Promise<void> {
    try {
      const prosPromise = this.pros.getAllPros();
      const addressDataPromise = this.getAddressData();
      const billsPromise = this.getFilteredBills(this.filters, false);
      this.form.patchValue({
        selectedStatus: 'pending'
      })
      const [pros] = await Promise.all([prosPromise, addressDataPromise, billsPromise]);
      this.proList = this.parseProList(pros);
      this.getTreeData();
      this.applySavedPropertyFilter();
      this.isDataLoaded = true;
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  parseProList(pros: any[]): any[] {
    return pros.map((pro) => ({
        value: pro.id,
        viewValue: pro?.name ? new TidyAbbreviateLastName().transform(pro.name) : '',
        teamId: pro?.main_team_id,
      }));
  }

  sortBillsByJobDate(bills: any[]): any[] {
    return bills.sort((a, b) => {
      const dateA = a.items?.[0]?.service_details?.date;
      const dateB = b.items?.[0]?.service_details?.date;
      if (!dateA && !dateB) {
        return 0;
      }
      if (!dateA) {
        return 1;
      }
      if (!dateB) {
        return -1;
      }
      return new Date(dateB).getTime() - new Date(dateA).getTime();
    });
  }

  buildRows(bills: any[]): void {
    this.rows = [];
    bills.map((item) => {
      const serviceDetails = item.items?.[0]?.service_details;
      const alreadyAdded = this.rows.find((row) => row.id === item.id);
      if (alreadyAdded) {
        const alreadyAddedIndex = this.rows.findIndex((row) => row.id === item.id);
        this.rows.splice(alreadyAddedIndex, 1);
      }
      this.rows.push({
        id: item.id,
        'Property': {
          value: this.getPropertyName(serviceDetails?.address) || '',
          action: this.goToProperty.bind(this, serviceDetails?.address?.id),
          color: this.addressResponse ? (this.addressResponse.find((address) => address.id === serviceDetails?.address?.id)?.color || '#1E2125') : '#1E2125',
          addressId: serviceDetails?.address?.id
        },
        'Pro': {
          value: new TidyAbbreviateLastName().transform(getSellerDisplay(item).name),
          action: this.goToPro.bind(this, getSellerDisplay(item).teamId, serviceDetails?.address?.id),
        },
        'Status': {
          value: new TranslationPipe().transform(item.full_paid ? 'Paid' : 'Pending'),
          action: null,
        },
        'Job': {
          value: serviceDetails?.name || '',
          action: null,
        },
        'Job Date': {
          value: this.getParsedJobDate(serviceDetails),
          action: null,
        },
        'Total Amount': {
          value: new TidyCurrencyPipe().transform(item.amount),
          action: null,
        },
        'last-column': {
          value: '',
          action: null,
        },
      });
    });
    this.currentRows = this.rows;
  }

  getParsedJobDate(serviceDetails: any): string {
    const parsedDate = new ParseDateTimePipe().transform(serviceDetails?.date, 'ddd MMM d');
    return parsedDate;
  }

  goToPro(id: number, addressId: number): void {
    if (this.showAboutThisPage) {
      return;
    }
    const params = {
      addressId,
      proId: id
    };
    this.goToPage(`my-pro/${id}`, params, MyProPage);
  }

  goToProperty(id: number): void {
    if (this.showAboutThisPage) {
      return;
    }
    this.navCtrl.navigateForward(`properties?propertyId=${id}`);
  }

  getPropertyName(address: any): string {
    if (address?.address_name) {
      return `${address.address_name  }, ${  address.zip}`;
    }
    return `${address.address  }, ${  address.zip}`;
  }

  buildHeaders(): void {
    this.headers = [
      'Property',
      'Pro',
      'Status',
      'Job',
      'Job Date',
      'Total Amount',
      'last-column'
    ];
  }

  sortChanged(sort) {
    if (this.showAboutThisPage) {
      return;
    }
    this.rows.sort((a, b) => {
      if (!a[sort.active]?.value || !b[sort.active]?.value) {
        return 0;
      }

      if (sort.active === 'Job Date') {
        const parseDate = (dateStr: string) => {
          const currentYear = LuxonDateTime.local().year;
          if (!dateStr.includes(',')) {
            return LuxonDateTime.fromFormat(`${dateStr  }, ${  currentYear}`, 'EEE MMM d, yyyy');
          }
          return LuxonDateTime.fromFormat(dateStr, 'EEE MMM d, yyyy');
        };

        try {
          const dateA = parseDate(a[sort.active].value).toJSDate().getTime();
          const dateB = parseDate(b[sort.active].value).toJSDate().getTime();
          return sort.direction === 'asc' ? dateA - dateB : dateB - dateA;
        } catch (e) {
          return 0;
        }
      } else {
        const valueA = this.parseRowNumberValue(a[sort.active].value);
        const valueB = this.parseRowNumberValue(b[sort.active].value);

        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return sort.direction === 'asc' || sort.direction === '' ? valueA - valueB : valueB - valueA;
        } else {
          const strA = String(valueA || '').toLowerCase();
          const strB = String(valueB || '').toLowerCase();
          if (strA < strB) {return sort.direction === 'asc' || sort.direction === '' ? -1 : 1;}
          if (strA > strB) {return sort.direction === 'asc' || sort.direction === '' ? 1 : -1;}
          return 0;
        }
      }
    });
    this.currentRows = this.rows;
  }

  parseRowNumberValue(value: string): string | number {
    if (value.includes('$')) {
      const numberValue = Number(value.replace(/\$|\.|\s/g, ''));
      return isNaN(numberValue) ? value : numberValue;
    }
    return value;
  }

  resetDefaultChips(params?: Partial<InvoiceFilterParams>) {
    if (params?.status?.includes('pending')) {
      const hasPendingStatusChip = this.chips?.find((chip) => chip.value === 'pending');
      if (!hasPendingStatusChip) {
        this.addChip({
          displayParentName: 'Status',
          parentName: 'status',
          name: 'pending',
          value: 'pending'
        });
        this.updateTree();
      }
    }
    const hasOnlyPendingStatus = params?.status?.length === 1 && params?.status?.includes('pending');
    if (!params?.address_ids && !params?.team_ids && hasOnlyPendingStatus) {
      const notKeeperChips = this.chips?.filter((chip) => chip.value !== 'pending' && chip.parentName !== 'pro' && chip.parentName !== 'propertyName');
      notKeeperChips?.forEach((chip) => {
        this.selectedFilters[chip.parentName] = this.selectedFilters[
          chip.parentName
        ].filter((value) => value !== chip.value);

        this.chips = this.chips.filter(
          (item) => !(item.parentName === chip.parentName && item.value === chip.value)
        );
        this.updateTree();
      });
      const propertyChips = this.chips?.filter((chip) => chip.parentName === 'propertyName')?.map((chip) => Number(chip.value)) || [];
      const proChips = this.chips?.filter((chip) => chip.parentName === 'pro')?.map((chip) => Number(chip.value)) || [];
      const foundPros = this.proList.filter((pro) => proChips.includes(pro.value));

      params.address_ids = propertyChips.length > 0 ? propertyChips : null;
      params.team_ids = foundPros.length > 0 ? foundPros.map((pro) => pro.teamId) : null;
    }
  }

  async getFilteredBills(params?: Partial<InvoiceFilterParams>, showLoadingSpinner = true): Promise<void> {
    const now = Date.now();
    const fetchKey = JSON.stringify({ params, pageSize: this.pageSize, pageIndex: this.pageIndex });
    if (fetchKey === this.lastBillsFetchKey && (now - this.lastBillsFetchTime) < this.BILLS_CACHE_DURATION_MS) {
      return;
    }
    this.lastBillsFetchTime = now;
    this.lastBillsFetchKey = fetchKey;
    this.resetDefaultChips(params);
    let loading = null;
    if (this.loaded && !this.routeReused && showLoadingSpinner) {
      this.pageIndex = 1;
      loading = await this.util.showLoading();
    }
    const billsMockData = this.onboardingDummyDataService.getBillsMockData();
    const bills = this.showAboutThisPage ? {body: billsMockData, totalRecords: billsMockData.length} : await this.invoices.getAllInvoices(params, this.pageSize, this.pageIndex);
    this.totalRows = Number(bills.totalRecords) || 100;
    if (showLoadingSpinner) {
      this.bills = this.sortBillsByJobDate([...bills.body]);
    } else {
      this.bills = this.sortBillsByJobDate([...this.bills, ...bills.body]);
    }
    const paidInvoiceId = await this.storage.retrieve('paidInvoiceId');
    if (paidInvoiceId && params.status.includes('pending')) {
      this.bills = this.bills.filter((bill) => bill.id !== paidInvoiceId);
      await this.storage.delete('paidInvoiceId');
    }
    this.buildRows(this.bills);
    if (this.tidyTable) {
      setTimeout(() => {
        if (this.tidyTable?.paginator) {
          this.tidyTable.paginator.length = this.totalRows;
          this.tidyTable.paginator.pageIndex = this.pageIndex - 1;
        }
      }, 500);
    }
    loading?.dismiss();
  }

  onFilterValueChange(event: number[] | string, type: string): void {
    if (this.showAboutThisPage) {
      return;
    }
    this.pageIndex = 1;
    if (type === 'pro') {
      const proIds = event as number[];
      const foundPros = this.proList.filter((pro) => proIds.includes(pro.value));
      this.filters['team_ids'] = foundPros.length > 0 ? foundPros.map((pro) => pro.teamId) : null;
    }
    if (type === 'status') {
      this.filters['status'] = event ? [event as string] : null;
    }
    if (type === 'address') {
      const addressIds = event as number[];
      this.filters['address_ids'] = addressIds.length > 0 ? addressIds : null;
    }
    this.getFilteredBills(this.filters);
  }

  getEmptyStateMessage(): string {
    const statusChip = this.chips?.find(c => c.parentName === 'status');
    if (statusChip) {
      return `No ${statusChip.name.toLowerCase()} bills`;
    }

    return 'No bills found';
  }

  selectCustomDateRange(selection) {
    if (this.showAboutThisPage) {
      return;
    }
    const start = new Date(selection.start);
    const end = new Date(selection.end);
    const startDate = LuxonDateTime.fromJSDate(start).toISODate();
    const endDate = LuxonDateTime.fromJSDate(end).toISODate();
    this.filters['jobs_since_date'] = startDate;
    this.filters['jobs_until_date'] = endDate;
    this.getFilteredBills(this.filters);
  }

  selectStringDateRange(selection) {
    if (this.showAboutThisPage) {
      return;
    }
    const today = LuxonDateTime.local();
    let startDate = today.toISODate();
    let endDate = today.toISODate();
    if (selection === 'last_week') {
      startDate = today.minus({ days: 7 }).toISODate();
    }
    if (selection === 'last_month') {
      startDate = today.minus({ months: 1 }).toISODate();
    }
    if (selection === 'all_time') {
      startDate = null;
      endDate = null;
    }
    this.filters['jobs_since_date'] = startDate;
    this.filters['jobs_until_date'] = endDate;
    this.getFilteredBills(this.filters);
  }

  onTableColumnClick(event) {
    if (this.showAboutThisPage) {
      return;
    }
    const foundBill = this.bills.find((bill) => bill.id === event.element.id);
    this.goToInvoicePage(foundBill);
  }

  async getAddressData() {
    this.addressFilter = await this.navCtrl.getParam('addressFilter') || await this.buildAddressFilter();
  }

  async buildAddressFilter() {
    this.addressResponse = await this.client.getAddresses();
    return this.client.parseAddressList(this.addressResponse, false);
  }

  async goToInvoicePage(invoice) {
    try {
      const seller = getSellerDisplay(invoice);
      const params: any = {
        invoiceId: invoice.id,
        hasAccount: true,
        proName: seller.name,
        proEmail: seller.email,
        proPhone: seller.phone,
        proTeamId: seller.teamId,
        proId: invoice?.seller?.data?.team_owner?.id ?? invoice?.homekeeper_data?.id,
        isLoggedIntoClientApp: true,
        successRoute: 'bills',
        invoice,
      }
      const path = invoice.full_paid ? `paid-bill/${invoice.id}` : `pending-bill/${invoice.id}`;
      const component = invoice.full_paid ? PaidInvoicePage : PendingInvoicePage;
      this.goToPage(path, params, component);
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  async goToPage(url, params, component) {
    if (this.showAboutThisPage) {
      return;
    }
    const isMobileResolution = !this.windowService.isDesktopRes;
    if (!isMobileResolution) {
      const isRightSidePanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isRightSidePanelAlreadyOpen) {
        this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component);
      }
      this.rightSidePanelService.panelClosed().pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(async () => {
        const didMakeBillChanges = await this.storage.retrieve('didMakeBillChanges');
        if (didMakeBillChanges) {
          this.bills = [];
          await this.storage.save('didMakeBillChanges', false);
          this.getPageData();
        }
      });
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }

  goToBillSettingsPage() {
    if (this.showAboutThisPage) {
      return;
    }
    this.goToPage('bill-settings', {}, BillSettingsPage);
  }

  goToCreateInvoice() {
    if (this.showAboutThisPage) {
      return;
    }
    this.goToPage('create-invoice', {}, CreateInvoicePage);
  }

  async handleInfiniteScroll(infiniteScrollEvent: InfiniteScrollCustomEvent) {
    if (this.bills.length >= this.totalRows) {
      infiniteScrollEvent?.target?.complete();
      return;
    }
    this.pageIndex += 1;
    await this.getFilteredBills(this.filters, false);
    if (infiniteScrollEvent?.target) {
      infiniteScrollEvent.target.complete();
    }
  }

  async pageChange() {
    if (this.showAboutThisPage) {
      return;
    }
    if (this.isGettingFilteredBills) {
      return;
    }
    if (this.bills.length >= this.totalRows) {
      return;
    }
    this.isGettingFilteredBills = true;
    if (this.tidyTable) {
      this.tidyTable.isLoading = true;
    }
    this.pageIndex += 1;
    try {
      await this.getFilteredBills(this.filters, false);
    } finally {
      if (this.tidyTable) {
        this.tidyTable.isLoading = false;
      }
      this.isGettingFilteredBills = false;
    }
  }

  getTreeData(): void {
    const treeData = [
      {
        name: 'Status',
        children: this.statusItems.map(status => ({
          name: status.viewValue,
          value: status.value.toString(),
          checked: status.value === 'pending',
          parentId: 0
        }))
      }
    ];
    if (this.proList.length > 0) {
      treeData.push({
        name: 'Pro',
        children: this.proList.map(pro => ({
          name: pro.viewValue,
          value: pro.value,
          checked: false,
          parentId: 1
        }))
      });
    }
    if (this.addressFilter.length > 0) {
      treeData.push({
        name: 'Property',
        children: this.addressFilter.map(address => ({
          name: address.viewValue,
          value: address.value,
          checked: false,
          parentId: 2
        }))
      });
    }
    treeData.forEach((parent, index) => {
      parent.children?.forEach((child) => {
        child.parentId = index;
      });
    });
    this.dataSource.data = treeData;
  }

  hasChild = (_: number, node: FlatNode) => node.expandable;

  checkNode(node: ItemNode, event: MatCheckboxChange): void {
    node.checked = event.checked;
    this.updateFilters(node, event.checked);
  }

  getNodeVariables(node: ItemNode) {
    const foundParentName = this.dataSource.data[node.data.parentId].name;
    const foundNodeValue = node.data.value;
    const matchedParentName = this.filterNamesReferences[foundParentName];
    return { foundParentName, foundNodeValue, matchedParentName };
  }

  updateFilters(node: ItemNode, isEnabled: boolean): void {
    if (!node?.data?.parentId && node?.data?.parentId !== 0) {
      return;
    }
    const { foundParentName, foundNodeValue, matchedParentName } =
      this.getNodeVariables(node);
    const chip: ChipFilter = {
      displayParentName: foundParentName,
      parentName: matchedParentName,
      name: node.name,
      value: foundNodeValue,
      icon: this.getChipIcon(matchedParentName),
      color: this.getChipColor(matchedParentName, foundNodeValue),
    };
    if (isEnabled) {
      this.addChip(chip);
    } else {
      this.removeChip(chip);
    }
  }

  private getChipIcon(parentName: string): string {
    switch (parentName) {
      case 'propertyName':
        return 'home';
      case 'status':
        return 'flag';
      case 'pro':
        return 'person';
      default:
        return '';
    }
  }

  private getChipColor(parentName: string, value: any): string {
    if (parentName === 'propertyName') {
      const address = this.addressResponse?.find((a) => a.id === Number(value));
      return address?.color || '#1E2125';
    }
    return '';
  }

  addChip(chip: ChipFilter): void {
    if (this.showAboutThisPage) {
      return;
    }
    if (!this.selectedFilters) {
      this.selectedFilters = {
        status: ['pending'],
        pro: [],
        propertyName: []
      };
    }
    if (chip.parentName === 'status') {
      this.selectedFilters.status = [chip.value.toString()];
      this.chips = this.chips.filter(c => c.parentName !== 'status');
    } else {
      this.selectedFilters[chip.parentName].push(Number(chip.value));
    }
    this.chips.push(chip);
    if (chip.parentName === 'pro') {
      this.onFilterValueChange(this.selectedFilters.pro, 'pro');
    } else if (chip.parentName === 'status') {
      this.onFilterValueChange(chip.value.toString(), 'status');
    } else if (chip.parentName === 'propertyName') {
      this.onFilterValueChange(this.selectedFilters.propertyName, 'address');
    }
    this.updateTree();
  }

  updateTree(): void {
    this.dataSource.data.forEach((parent) => {
      parent.children.forEach((child) => {
        if (parent.name === 'Status') {
          child.checked = this.selectedFilters.status.includes(child.value.toString());
        } else if (
          this.selectedFilters[
            this.filterNamesReferences[parent.name]
          ]?.includes(Number(child.value))
        ) {
          child.checked = true;
        } else {
          child.checked = false;
        }
      });
    });
  }

  removeChip(chip: ChipFilter): void {
    if (this.showAboutThisPage) {
      return;
    }
    this.chips = this.chips.filter(
      (item) => !(item.parentName === chip.parentName && item.value === chip.value)
    );
    if (chip.parentName === 'status') {
      this.selectedFilters.status = [];
      this.onFilterValueChange(null, 'status');
    } else {
      this.selectedFilters[chip.parentName] = this.selectedFilters[
        chip.parentName
      ].filter((value) => value !== chip.value);
      if (chip.parentName === 'pro') {
        this.onFilterValueChange(this.selectedFilters.pro, 'pro');
      } else if (chip.parentName === 'propertyName') {
        this.onFilterValueChange(this.selectedFilters.propertyName, 'address');
      }
    }
    this.updateTree();
  }

  getNodeChecked(node: ItemNode): boolean {
    if (!node?.data?.parentId && node?.data?.parentId !== 0) {
      return false;
    }
    const { foundParentName, foundNodeValue, matchedParentName } = this.getNodeVariables(node);
    if (matchedParentName === 'status') {
      return this.selectedFilters.status.includes(foundNodeValue.toString());
    }
    return this.selectedFilters?.[matchedParentName]?.includes(Number(foundNodeValue));
  }

  async exportCSV() {
    if (this.showAboutThisPage) {
      return;
    }
    if (!this.tidyTable) {
      this.util.showError('No data to export', 5000);
      return;
    }
    try {
      const loadingIndicator = await this.util.showLoading({message: 'Preparing export...'});
      const allBillsMockData = this.onboardingDummyDataService.getBillsMockData();
      const allBills = this.showAboutThisPage ?
        {body: allBillsMockData, totalRecords: allBillsMockData.length} :
        await this.invoices.getAllInvoicesPaginated(this.filters);
      const allBillsData = this.sortBillsByJobDate([...allBills.body]);
      const tempRows = [];
      allBillsData.forEach((item) => {
        const serviceDetails = item.items?.[0]?.service_details;
        tempRows.push({
          id: item.id,
          'Property': {
            value: this.getPropertyName(serviceDetails?.address) || '',
            action: this.goToProperty.bind(this, serviceDetails?.address?.id),
            color: this.addressResponse ? (this.addressResponse.find((address) => address.id === serviceDetails?.address?.id)?.color || '#1E2125') : '#1E2125',
            addressId: serviceDetails?.address?.id
          },
          'Pro': {
            value: new TidyAbbreviateLastName().transform(getSellerDisplay(item).name),
            action: this.goToPro.bind(this, getSellerDisplay(item).teamId, serviceDetails?.address?.id),
          },
          'Status': {
            value: new TranslationPipe().transform(item.full_paid ? 'Paid' : 'Pending'),
            action: null,
          },
          'Job': {
            value: serviceDetails?.name || '',
            action: null,
          },
          'Job Date': {
            value: serviceDetails?.date ? LuxonDateTime.fromISO(serviceDetails.date).toFormat('yyyy-MM-dd') : this.getParsedJobDate(serviceDetails),
            action: null,
          },
          'Total Amount': {
            value: new TidyCurrencyPipe().transform(item.amount),
            action: null,
          },
          'last-column': {
            value: '',
            action: null,
          },
        });
      });
      const exportDataSource = new MatTableDataSource(tempRows);
      this.tidyTable.exportCsv(exportDataSource);
      loadingIndicator?.dismiss();
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  private applySavedPropertyFilter(): void {
    if (!this.selectedFilters.propertyName || this.selectedFilters.propertyName.length === 0) {
      return;
    }
    this.selectedFilters.propertyName.forEach(addressId => {
      const numericId = Number(addressId);
      const foundAddress = this.addressFilter.find(addr => Number(addr.value) === numericId);
      const existingChip = this.chips.find(c => c.parentName === 'propertyName' && Number(c.value) === numericId);
      if (foundAddress && !existingChip) {
        const addressObj = this.addressResponse?.find((a) => a.id === numericId);
        const chip: ChipFilter = {
          displayParentName: 'Property',
          parentName: 'propertyName',
          name: foundAddress.viewValue,
          value: numericId,
          icon: 'home',
          color: addressObj?.color || '#1E2125',
        };
        this.chips.push(chip);
      }
    });
    this.updateTree();
  }

}
